import type {
  MaintenanceScheduleEntry,
  UnitSystem,
  VehicleRecalls,
  VinDecodeResult,
} from '@cartrack/shared';
import { useAuthStore } from './auth-store';

const API_BASE = (import.meta.env.VITE_API_BASE as string | undefined) ?? '/api';

export class ApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
    readonly body: unknown,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const token = useAuthStore.getState().session?.access_token;
  const headers: Record<string, string> = {
    'content-type': 'application/json',
    ...(init.headers as Record<string, string> | undefined),
  };
  if (token) headers['authorization'] = `Bearer ${token}`;
  const res = await fetch(`${API_BASE}${path}`, { ...init, headers });
  const text = await res.text();
  const body = text ? (JSON.parse(text) as unknown) : null;
  if (!res.ok) {
    throw new ApiError(`${res.status} ${res.statusText}`, res.status, body);
  }
  return body as T;
}

export interface Vehicle {
  id: string;
  userId: string;
  make: string;
  model: string;
  year: number;
  trim: string | null;
  engine: string | null;
  vin: string | null;
  nickname: string | null;
  photoUrl: string | null;
  currentMileage: number;
  mileageUpdatedAt: string;
  dailyMilesEstimate: number | null;
  garageCountry: string;
  garageStateProvince: string | null;
  garagePostalCode: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface ServiceLogEntry {
  id: string;
  vehicleId: string;
  date: string;
  mileage: number;
  serviceType: string;
  cost: number | null;
  mechanic: string | null;
  shop: string | null;
  parts: string[];
  warranty: string | null;
  notes: string | null;
  receiptUrl: string | null;
  createdAt: string;
}

/** Daily chat quota, mirrors the backend `ChatQuota`. */
export interface ChatQuota {
  used: number;
  limit: number;
  remaining: number;
}

/** A persisted chat message as returned by `GET /vehicles/:id/chat`. */
export interface ChatMessage {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  codesCited: string[];
  recallsCited: string[];
  createdAt: string;
}

/** The answer envelope returned by `POST /vehicles/:id/chat`. */
export interface ChatReply {
  reply: string;
  codesCited: string[];
  recallsCited: string[];
  usage: { inputTokens: number; outputTokens: number };
  quota: ChatQuota;
}

export const api = {
  listVehicles: () => request<Vehicle[]>('/vehicles'),
  getVehicle: (id: string) => request<Vehicle>(`/vehicles/${id}`),
  getSchedule: (id: string) =>
    request<MaintenanceScheduleEntry[]>(`/vehicles/${id}/schedule`),
  listLogs: (id: string) => request<ServiceLogEntry[]>(`/vehicles/${id}/logs`),
  createLog: (
    id: string,
    dto: {
      date: string;
      mileage: number;
      serviceType: string;
      cost?: number;
      mechanic?: string;
      notes?: string;
      receiptUrl?: string;
    },
  ) =>
    request<ServiceLogEntry>(`/vehicles/${id}/logs`, {
      method: 'POST',
      body: JSON.stringify(dto),
    }),
  deleteLog: (vehicleId: string, logId: string) =>
    request<void>(`/vehicles/${vehicleId}/logs/${logId}`, { method: 'DELETE' }),
  getRecalls: (id: string) => request<VehicleRecalls>(`/vehicles/${id}/recalls`),
  getChatHistory: (vehicleId: string) =>
    request<ChatMessage[]>(`/vehicles/${vehicleId}/chat`),
  getChatQuota: (vehicleId: string) =>
    request<ChatQuota>(`/vehicles/${vehicleId}/chat/quota`),
  sendChatMessage: (vehicleId: string, message: string) =>
    request<ChatReply>(`/vehicles/${vehicleId}/chat`, {
      method: 'POST',
      body: JSON.stringify({ message }),
    }),
  getMe: () =>
    request<{
      id: string;
      email: string;
      role: 'USER' | 'ADMIN';
      plan: 'free' | 'premium' | 'fleet';
      reminders: {
        leadDays: number;
        leadMiles: number;
        repeatWeeks: number;
        followUpEnabled: boolean;
        followUpDays: number;
      };
      unitSystem: UnitSystem;
      displayName: string | null;
      country: string;
      stateProvince: string | null;
      postalCode: string | null;
    }>('/users/me'),
  updateMe: (dto: {
    unitSystem?: UnitSystem;
    displayName?: string;
    country?: string;
    stateProvince?: string | null;
    postalCode?: string | null;
    reminderPreset?: 'relaxed' | 'standard' | 'tight';
  }) =>
    request<{
      id: string;
      email: string;
      role: 'USER' | 'ADMIN';
      plan: 'free' | 'premium' | 'fleet';
      reminders: {
        leadDays: number;
        leadMiles: number;
        repeatWeeks: number;
        followUpEnabled: boolean;
        followUpDays: number;
      };
      unitSystem: UnitSystem;
      displayName: string | null;
      country: string;
      stateProvince: string | null;
      postalCode: string | null;
    }>('/users/me', { method: 'PATCH', body: JSON.stringify(dto) }),
  decodeVin: (vin: string) =>
    request<VinDecodeResult>(`/vehicles/decode-vin?vin=${encodeURIComponent(vin)}`),
  createVehicle: (
    dto: Partial<Vehicle> & ({ vin: string } | { make: string; model: string; year: number }),
  ) =>
    request<Vehicle>('/vehicles', {
      method: 'POST',
      body: JSON.stringify(dto),
    }),
  updateVehicle: (id: string, dto: Partial<Vehicle>) =>
    request<Vehicle>(`/vehicles/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(dto),
    }),
  updateMileage: (id: string, mileage: number) =>
    request<Vehicle>(`/vehicles/${id}/mileage`, {
      method: 'PATCH',
      body: JSON.stringify({ mileage }),
    }),
  deleteVehicle: (id: string) =>
    request<void>(`/vehicles/${id}`, { method: 'DELETE' }),
  listScheduledServices: (vehicleId: string) =>
    request<ScheduledServiceDto[]>(`/vehicles/${vehicleId}/scheduled-services`),
  updateScheduledService: (
    vehicleId: string,
    id: string,
    dto: { state?: ScheduledServiceState; notes?: string | null },
  ) =>
    request<ScheduledServiceDto>(
      `/vehicles/${vehicleId}/scheduled-services/${id}`,
      { method: 'PATCH', body: JSON.stringify(dto) },
    ),
  deleteMe: () => request<void>('/users/me', { method: 'DELETE' }),
  adminStats: () => request<AdminStats>('/admin/stats'),
  adminListUsers: (opts: { limit?: number; offset?: number; q?: string } = {}) => {
    const qs = new URLSearchParams();
    if (opts.limit !== undefined) qs.set('limit', String(opts.limit));
    if (opts.offset !== undefined) qs.set('offset', String(opts.offset));
    if (opts.q) qs.set('q', opts.q);
    const suffix = qs.toString() ? `?${qs}` : '';
    return request<AdminUsersResponse>(`/admin/users${suffix}`);
  },
  listNotifications: (opts: { unread?: boolean } = {}) =>
    request<AppNotificationDto[]>(
      `/notifications${opts.unread ? '?unread=true' : ''}`,
    ),
  getUnreadNotificationCount: () =>
    request<{ count: number }>('/notifications/unread-count'),
  markNotificationRead: (id: string) =>
    request<AppNotificationDto>(`/notifications/${id}/read`, { method: 'PATCH' }),
  markAllNotificationsRead: () =>
    request<{ updated: number }>('/notifications/read-all', { method: 'PATCH' }),
  sendContactMessage: (dto: { subject?: string; body: string }) =>
    request<ContactMessageDto>('/contact', {
      method: 'POST',
      body: JSON.stringify(dto),
    }),
  adminListContact: () => request<ContactMessageDto[]>('/admin/contact'),
  adminVrdbStatus: () => request<VrdbStatus>('/admin/vrdb-status'),
  adminRequestVrdbRefresh: () =>
    request<VrdbRefreshRequestResult>('/admin/vrdb-refresh', {
      method: 'POST',
    }),
};

export interface ContactMessageDto {
  id: string;
  userId: string;
  fromEmail: string;
  subject: string | null;
  body: string;
  status: 'new' | 'read';
  createdAt: string;
}

export type AppNotificationType =
  | 'service_due_soon'
  | 'service_overdue'
  | 'recall'
  | 'deal'
  | 'seasonal';

export interface AppNotificationDto {
  id: string;
  type: AppNotificationType;
  title: string;
  body: string;
  data: Record<string, unknown>;
  read: boolean;
  sentAt: string;
  vehicleId: string | null;
}

export type ScheduledServiceState = 'PENDING' | 'SERVICED' | 'NO_DATA';

export interface AdminStats {
  users: number;
  admins: number;
  vehicles: number;
  serviceLogs: number;
  scheduledServices: number;
  notifications: { total: number; unread: number };
  cachedSchedules: number;
}

export interface VrdbStatus {
  lastSuccessAt: string | null;
  recallCount: number | null;
  modelCount: number | null;
  daysSinceSuccess: number | null;
  isStale: boolean;
  pending: boolean;
  lastAttemptStatus: string | null;
  lastAttemptError: string | null;
}

export interface VrdbRefreshRequestResult {
  id: string;
  status: string;
  alreadyQueued: boolean;
}

export interface AdminUserRow {
  id: string;
  email: string;
  role: 'USER' | 'ADMIN';
  plan: string;
  unitSystem: string;
  country: string;
  createdAt: string;
  vehicleCount: number;
}

export interface AdminUsersResponse {
  total: number;
  rows: AdminUserRow[];
}

export interface ScheduledServiceDto {
  id: string;
  vehicleId: string;
  serviceType: string;
  dueMileage: number;
  dueMonths: number | null;
  state: ScheduledServiceState;
  servicedAt: string | null;
  serviceLogId: string | null;
  notes: string | null;
  createdAt: string;
  updatedAt: string;
}
